import React from 'react';
import { Sparkles, Check, Plus, AlertCircle } from 'lucide-react';
import { CustomProject, ProjectPage } from '../../types/productBuilder';
import { ProjectSuggestion } from '../types';

interface SuggestionsProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Suggestions: React.FC<SuggestionsProps> = ({ project, onUpdateProject, onAddLog }) => {
  
  // Custom suggestions based on the project's selected industry
  const getSuggestions = (): ProjectSuggestion[] => {
    const isTech = project.industry?.toLowerCase().includes('tech') || project.industry?.toLowerCase().includes('software');
    const isRetail = project.industry?.toLowerCase().includes('retail') || project.industry?.toLowerCase().includes('commerce') || project.industry?.toLowerCase().includes('b2c');
    
    if (isTech) {
      return [
        {
          id: 's_tech_1',
          category: 'Automated Compliance',
          title: 'GDPR Data Ledger Grid',
          benefit: 'Integrates real-time regulatory compliance indicators for high-speed UK data protection auditing.',
          sectionsToApply: ['GDPR Security Audit Roll', 'Secure Client Certificate Block']
        },
        {
          id: 's_tech_2',
          category: 'Aesthetic Styling Analytics',
          title: 'Sub-Penny Billing Activity Map',
          benefit: 'Injects high-density telemetry layout row that charts micro-settlements and active operational nodes.',
          sectionsToApply: ['High-Frequency Financial Activity', 'Fiscal Strike Flowchart']
        },
        {
          id: 's_tech_3',
          category: 'Administrative Workflows',
          title: 'WorkSuite SLA Performance Indicator',
          benefit: 'Appends a dedicated live target performance tracking board integrated directly into the workspace.',
          sectionsToApply: ['Target Performance Metric Grid', 'SLA Regulatory Footnotes']
        }
      ];
    } else if (isRetail) {
      return [
        {
          id: 's_ret_1',
          category: 'Revenue Optimization',
          title: 'Stripe Instant Billing Node',
          benefit: 'Enables high-fidelity Checkout rows pre-configured with direct routing to Secure payment provider.',
          sectionsToApply: ['Secure Checkout Payment Module', 'Order Allocation Invoice Ledger']
        },
        {
          id: 's_ret_2',
          category: 'Customer Conversion',
          title: 'Managed Connected Launch Bundle Drawer',
          benefit: 'Appends Introductory Discount pricing and bundle details overlay to maximize onboarding volume.',
          sectionsToApply: ['Connected Launch Pitch Banner', 'Managed Subscription Benefit Cards']
        },
        {
          id: 's_ret_3',
          category: 'Social Proof',
          title: 'Affiliated Charitable Splitter',
          benefit: 'Promotes direct corporate transparency allocations split for UK charities.',
          sectionsToApply: ['Charitable Levy Ticker Grid', 'Dunfermline Regional Support Badge']
        }
      ];
    } else {
      // General fallbacks
      return [
        {
          id: 's_gen_1',
          category: 'Aesthetic Structure',
          title: 'Atmospheric Interactive Header',
          benefit: 'Adds modern ambient background with custom color swatches and neon glow controls.',
          sectionsToApply: ['Atmospheric Header', 'Interactive Operations Ticker']
        },
        {
          id: 's_gen_2',
          category: 'Operations',
          title: 'Risk Compliance Radar Grid',
          benefit: 'Renders automated risk and GDPR checkers. Highly optimized for high-level UK entities.',
          sectionsToApply: ['NEXORAX Guardian Scan Radar', 'Compliance Metric Cards']
        },
        {
          id: 's_gen_3',
          category: 'Workforce',
          title: 'Pakistan Remote Node Coordinator',
          benefit: 'Injects staff tracking dashboard with high-density instructions standard.',
          sectionsToApply: ['Pakistan Node Sync Status', 'Remote Staff Instructions Timeline']
        }
      ];
    }
  };

  const applySuggestion = (sug: ProjectSuggestion) => {
    // We add the sections to the FIRST page of the project
    if (project.pages.length === 0) return;
    
    const firstPage = project.pages[0];
    const updatedSections = [...firstPage.layoutSections];
    let addedCount = 0;

    sug.sectionsToApply.forEach(sec => {
      if (!updatedSections.includes(sec)) {
        updatedSections.push(sec);
        addedCount++;
      }
    });

    if (addedCount === 0) {
      alert(`The suggestions in "${sug.title}" have already been incorporated into your primary layouts context.`);
      return;
    }

    const updatedPages = project.pages.map((p, idx) => 
      idx === 0 ? { ...p, layoutSections: updatedSections } : p
    );

    onUpdateProject({ ...project, pages: updatedPages });
    onAddLog('Product Builder', `Applied Suggestion Group: "${sug.title}"`, 'SUCCESS', `Added ${addedCount} custom layout sections.`);
  };

  const suggestions = getSuggestions();

  return (
    <div className="npx-builder-suggestions-container space-y-6 text-left animate-fade-in">
      <div className="border-b border-white/5 pb-4">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-[#00ffff] animate-pulse" />
          <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">3. Suggestion Universe</h2>
        </div>
        <p className="text-xs text-zinc-400 mt-1">Prompt-free micro-recommendation engine tracking your industry vertical (<strong>{project.industry}</strong>) to suggest layout additions instantly.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4.5">
        {suggestions.map((sug) => (
          <div
            key={sug.id}
            className="flex flex-col justify-between p-5 rounded-2xl bg-black/40 border border-white/5 hover:border-zinc-800 transition-all text-left"
          >
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[7.5px] font-mono tracking-wider font-extrabold text-cyan-400 uppercase bg-[#00ffff]/10 px-2 py-0.5 rounded border border-[#00ffff]/20">
                  {sug.category}
                </span>
              </div>
              <h3 className="text-xs font-bold text-white uppercase font-mono tracking-wider leading-relaxed">{sug.title}</h3>
              <p className="text-[10px] text-zinc-400 leading-normal font-sans normal-case">{sug.benefit}</p>
              
              <div className="pt-2">
                <span className="text-[7.5px] font-mono uppercase text-zinc-500 font-bold block mb-1">COMPONENTS TO REGISTER:</span>
                <div className="flex flex-wrap gap-1">
                  {sug.sectionsToApply.map((sec, idx) => (
                    <span key={idx} className="text-[7.5px] font-mono text-zinc-500 bg-zinc-950 border border-white/5 px-2 py-0.5 rounded uppercase">
                      ◈ {sec}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => applySuggestion(sug)}
              className="mt-6 font-mono text-[9.5px] font-black uppercase text-[#020202] hover:text-white bg-gradient-to-r from-cyan-400 to-purple-500 py-2 rounded-xl text-center cursor-pointer transition-colors hover:from-cyan-900 hover:to-indigo-950 border border-cyan-500/10 flex items-center justify-center gap-1"
            >
              <Plus size={11} className="text-current" />
              <span>Apply Suggestion</span>
            </button>
          </div>
        ))}
      </div>

      <div className="p-4 bg-zinc-950/60 border border-white/5 rounded-2xl flex items-start gap-3 text-left">
        <AlertCircle size={15} className="text-[#00ffff] shrink-0 mt-0.5 animate-pulse" />
        <div className="space-y-1">
          <h4 className="text-[11px] font-mono text-white uppercase font-bold">Zero-prompt integration assurance</h4>
          <p className="text-[9.5px] text-zinc-500 leading-relaxed font-sans normal-case">
            Unlike standard AI assistance generators, the Suggestion Universe avoids unauthenticated raw content prompting. It selects from compliance-cleared structural layouts specifically pre-compiled for UK regulatory entities, protecting client secrets fully from external telemetry scopes.
          </p>
        </div>
      </div>
    </div>
  );
};
